<template>
  <Sheet :open="true" @update:open="handleCancel">
    <SheetContent class="gap-0 pb-2">
      <SheetHeader padded>
        <SheetTitle size="lg">{{ actionName }}</SheetTitle>
      </SheetHeader>

      <Separator />

      <SheetBody padded class="flex-1 min-h-0 overflow-y-auto space-y-6 py-4">
        <ScheduleInterviewInput :input="scheduleInterviewInput" optional />

        <template v-if="!isEditingCandidateOutreach">
          <Separator />

          <div>
            <Label class="font-medium">Add a note (optional)</Label>
            <NoteInput v-model:content="note" v-model:note-type="noteType" class="mt-2" />
          </div>

          <Separator />

          <div>
            <Label class="font-medium">Add any tasks (optional)</Label>

            <div v-if="tasks.length" class="mt-2 space-y-2">
              <div
                v-for="(task, index) in tasks"
                :key="index"
                class="flex items-center justify-between border border-gray-200 rounded-md px-3 py-2"
              >
                <span class="text-sm truncate">{{ task.name }}</span>
                <div class="flex items-center gap-2 shrink-0 ml-2">
                  <span class="text-sm text-gray-400">{{ task.assigneeLabel }}</span>
                  <span v-if="task.dueDate" class="text-sm text-gray-400">{{ task.dueDate }}</span>
                  <button class="text-gray-400 hover:text-gray-600" @click="removeTask(index)">&times;</button>
                </div>
              </div>
            </div>

            <OneOffTaskInput :controller="taskInput" action-title="Add another task" class="mt-2" @action="addTask" />
          </div>
        </template>
      </SheetBody>

      <SheetFooter bordered class="flex-row justify-end gap-3">
        <template v-if="showSchedulingModeBack">
          <Button variant="ghost" class="mr-auto" @click="schedulingMode = 'none'">Back</Button>
        </template>
        <template v-if="!isEditingCandidateOutreach">
          <Button variant="outline" @click="handleCancel">Cancel</Button>
          <Button :disabled="isSubmitting" @click="handleAction">{{ actionName }}</Button>
        </template>
        <template v-if="isEditingCandidateOutreach">
          <Button @click="navigateToCandidateReview">Continue</Button>
        </template>
      </SheetFooter>
    </SheetContent>
  </Sheet>
</template>

<script setup lang="ts">
import { computed, ref, toRef } from 'vue';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetBody, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { OneOffTaskInput } from '@/components/shared/OneOffTaskInput';
import NoteInput from '../NoteInput.vue';
import type { ContactStage } from '@/api/entity_types/Contact';
import type { UseMoveContactStageReturn } from '../ContactStageActions/useMoveContactStage';
import { Api } from '@/api';
import ScheduleInterviewInput from '@/components/shared/ScheduleInterviewInput/ScheduleInterviewInput.vue';
import { useScheduleInterviewInput } from '@/components/shared/ScheduleInterviewInput/useScheduleInterviewInput';
import { useMeetingStageSheetTasks } from './useMeetingStageSheetTasks';
import type { ContactDetail } from '../ContactDetail';
import { getNoteType, type NoteTypeOption } from '../noteTypes';

type Props = {
  contact: Pick<ContactDetail, 'id' | 'firstName' | 'lastName' | 'email' | 'phone' | 'canReceiveSms'>;
  contactStage: ContactStage;
  moveContactStage: UseMoveContactStageReturn;
};
const props = defineProps<Props>();

const eventType = computed(() => {
  if (props.contactStage === 'Phone Screen') return 'phone_screen';
  if (props.contactStage === 'Interview') return 'interview';
  return 'phone_screen';
});

const scheduleInterviewInput = useScheduleInterviewInput({
  contact: toRef(props, 'contact'),
  event_type: eventType,
});
const { isEditingCandidateOutreach, schedulingMode, showSchedulingModeBack, navigateToCandidateReview } =
  scheduleInterviewInput;

const { taskInput, tasks, addTask, removeTask, validateTasks, submitAllTasks } = useMeetingStageSheetTasks();
const note = ref('');
const noteType = ref<NoteTypeOption>('General note');
const isSubmitting = ref(false);

const actionName = computed(() => `Move to ${props.contactStage}`);

const handleAction = async (): Promise<void> => {
  if (isSubmitting.value) return;

  if (!scheduleInterviewInput.validate()) return;
  if (!validateTasks()) return;

  isSubmitting.value = true;

  try {
    const success = await props.moveContactStage.updateContactStage(props.contactStage);
    if (!success) return;

    if (note.value.trim()) {
      await Api.ContactNotes.createContactNote({
        contact_id: props.contact.id,
        content: note.value,
        note_type: getNoteType(noteType.value),
      });
    }

    await submitAllTasks(props.contact.id);
    await scheduleInterviewInput.submit();

    emit('stage-changed', props.contactStage);
  } finally {
    isSubmitting.value = false;
  }
};

const handleCancel = (): void => {
  emit('handle-cancel');
};

const emit = defineEmits<{
  'stage-changed': [stage: ContactStage];
  'handle-cancel': [];
}>();
</script>
